import { useEffect, useState } from "react";

import { Radio, Spinner, VisualPicker } from "@salesforce/design-system-react";

import { Button } from "@vimeo/iris/components";

import { ListResponse, Thumbnail, ThumbnailType, VimeoAsset } from "vimeo";

import {
    client,
    ERROR_CODE_VIDEO_NOT_FOUND_USER_DOES_NOT_OWN_VIDEO,
} from "../../vimeoClient";

import { openCreateThumbnailWidget, appOrigin } from "../../common";
import { EventName, sendAnalyticsEvent } from "../../analytics/common";

interface Props {
    videoUri: string;
    onSelect: (
        type: ThumbnailType,
        imgUrl: string,
        imgUrlWithPlayButton: string,
        isPortrait: boolean
    ) => void;
}

export function Thumbnails(props: Props) {
    const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
    const [loading, setLoading] = useState(false);
    const [videoId, setVideoId] = useState<string>();
    const [showAccessError, setShowAccessError] = useState(false);

    async function getThumbnails() {
        const resp = await client.get<ListResponse<VimeoAsset>>(
            `/api/vimeo${props.videoUri}/assets?sizes=178,800&no_padding=true`
        );
        setThumbnails(
            resp.data.data
                .filter((asset) => asset.type === "image")
                .map((a) => a.item as Thumbnail)
        );
    }

    useEffect(() => {
        if (!props.videoUri) {
            return;
        }

        setVideoId(props.videoUri.replace("/videos/", ""));
        setLoading(true);

        getThumbnails()
            .catch((e) => {
                if (
                    e.response?.data?.error_code ===
                    ERROR_CODE_VIDEO_NOT_FOUND_USER_DOES_NOT_OWN_VIDEO
                ) {
                    setShowAccessError(true);
                }
                console.error(e);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.videoUri]);

    function onChange(event: any) {
        const index = event.target.value;
        const thumbnail = thumbnails[index];

        props.onSelect(
            "thumbnail",
            thumbnail.sizes[1].link,
            thumbnail.sizes[1].link_with_play_button,
            thumbnail.sizes[1].height > thumbnail.sizes[1].width
        );
    }

    const thumbnailEls = thumbnails?.map((t, index) => (
        <Radio
            key={index}
            value={`${index}`}
            onRenderVisualPicker={() => (
                <span>
                    <img
                        style={{
                            maxWidth:
                                t.sizes[1].height > t.sizes[1].width
                                    ? "40%"
                                    : "100%",
                        }}
                        src={t.sizes[1].link}
                        alt=""
                    />
                </span>
            )}
            onChange={onChange}
        />
    ));

    async function onVisibilityChange() {
        if (document.visibilityState !== "visible") {
            return;
        }

        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        setLoading(true);
        await getThumbnails();
        setLoading(false);
    }

    async function onMessage(e: MessageEvent) {
        if (e.origin !== appOrigin) {
            return;
        }

        setLoading(true);
        await getThumbnails();
        setLoading(false);
    }

    async function onCreateThumbnailClicked() {
        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "open_thumbnail_modal",
            location: "embed_in_email",
            video_id: videoId,
            cta: "create_thumbnail",
        });

        const opened = await openCreateThumbnailWidget(videoId);

        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        if (!opened) {
            return;
        }

        opened.onmessage = onMessage;
    }

    if (showAccessError) {
        return (
            <div className="slds-var-m-top_large">
                Unable to embed in email<br />
                Viewer cannot Embed in Email. <br />
                Contact your admin to change your permissions.
            </div>
        );
    }

    return (
        <div>
            {loading && <Spinner />}
            <div style={{ display: "flex", justifyContent: "end" }}>
                <Button format="secondary" onClick={onCreateThumbnailClicked}>
                    Create thumbnail
                </Button>
            </div>

            {thumbnailEls && thumbnailEls.length > 0 && (
                <VisualPicker className="thumbnails-visual-picker">
                    {thumbnailEls}
                </VisualPicker>
            )}
            {!loading && (!thumbnailEls || !thumbnailEls.length) && (
                <div className="slds-var-m-top_large">
                    You have no thumbnails created.
                </div>
            )}
        </div>
    );
}
